import React, { useState, useEffect, useRef } from 'react';
import './css/GlobalSearch.css';

const GlobalSearch = ({ onNavigate }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [byType, setByType] = useState({});
  const searchRef = useRef(null);
  const debounceTimer = useRef(null);

  // Fermer les résultats si on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Recherche avec debounce
  const performSearch = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/search/global?q=${encodeURIComponent(searchQuery)}&limit=20`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setResults(data.results || []);
        setByType(data.by_type || {});
        setShowResults(true);
        setSelectedIndex(0);
      }
    } catch (error) {
      console.error('Erreur de recherche:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Gestion du changement de requête
  const handleQueryChange = (e) => {
    const value = e.target.value;
    setQuery(value);

    // Debounce la recherche
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      performSearch(value);
    }, 300);
  };

  // Gestion des touches clavier
  const handleKeyDown = (e) => {
    if (!showResults || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (results[selectedIndex]) {
          handleResultClick(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowResults(false);
        break;
      default:
        break;
    }
  };

  // Clic sur un résultat
  const handleResultClick = (result) => {
    setShowResults(false);
    setQuery('');
    
    // Extraire les informations de navigation
    const urlParts = result.url.split('/');
    const module = urlParts[1]; // "documents", "opportunities", "operations", "clients"
    const id = urlParts[2];

    // Naviguer vers le module approprié
    if (onNavigate) {
      onNavigate(module, id);
    }
  };

  // Icône selon le type
  const getTypeIcon = (type) => {
    switch (type) {
      case 'document':
        return '📄';
      case 'opportunity':
        return '💼';
      case 'operation':
        return '🚚';
      case 'client':
        return '👤';
      default:
        return '📌';
    }
  };

  // Label du type
  const getTypeLabel = (type) => {
    switch (type) {
      case 'document':
        return 'Document';
      case 'opportunity':
        return 'Opportunité';
      case 'operation':
        return 'Opération';
      case 'client':
        return 'Client';
      default:
        return type;
    }
  };

  // Format de l'affichage selon le type
  const formatAmount = (amount) => {
    if (!amount) return '';
    return `${amount.toLocaleString('fr-FR')} FCFA`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <div className="global-search-container" ref={searchRef}>
      <div className="search-input-wrapper">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          className="global-search-input"
          placeholder="Rechercher partout... (Ctrl+K)"
          value={query}
          onChange={handleQueryChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (query.length >= 2 && results.length > 0) {
              setShowResults(true);
            }
          }}
        />
        {isLoading && <span className="search-loader">⏳</span>}
      </div>

      {showResults && results.length > 0 && (
        <div className="global-search-results">
          <div className="search-results-header">
            <span>Résultats ({results.length})</span>
            <div className="results-by-type">
              {Object.entries(byType).map(([type, count]) => (
                <span key={type} className="type-badge">
                  {getTypeIcon(type)} {count}
                </span>
              ))}
            </div>
          </div>

          <div className="search-results-list">
            {results.map((result, index) => (
              <div
                key={result.id}
                className={`search-result-item ${index === selectedIndex ? 'selected' : ''}`}
                onClick={() => handleResultClick(result)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="result-icon">{getTypeIcon(result.type)}</div>
                <div className="result-content">
                  <div className="result-main">
                    <span 
                      className="result-title"
                      dangerouslySetInnerHTML={{ 
                        __html: result.highlight.title || result.highlight.reference || result.title 
                      }}
                    />
                    {result.status && (
                      <span className={`result-status status-${result.status}`}>
                        {result.status}
                      </span>
                    )}
                  </div>
                  <div className="result-details">
                    <span className="result-type">{getTypeLabel(result.type)}</span>
                    {result.subtitle && (
                      <>
                        <span className="separator">•</span>
                        <span 
                          dangerouslySetInnerHTML={{ 
                            __html: result.highlight.client || result.subtitle 
                          }}
                        />
                      </>
                    )}
                    {result.amount && (
                      <>
                        <span className="separator">•</span>
                        <span className="result-amount">{formatAmount(result.amount)}</span>
                      </>
                    )}
                    {result.date && (
                      <>
                        <span className="separator">•</span>
                        <span className="result-date">{formatDate(result.date)}</span>
                      </>
                    )}
                  </div>
                  {result.description && (
                    <div 
                      className="result-description"
                      dangerouslySetInnerHTML={{ 
                        __html: result.highlight.description || result.description 
                      }}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="search-results-footer">
            <span className="keyboard-hint">
              ↑↓ Naviguer • ↵ Sélectionner • Esc Fermer
            </span>
          </div>
        </div>
      )}

      {showResults && query.length >= 2 && results.length === 0 && !isLoading && (
        <div className="global-search-results">
          <div className="search-no-results">
            <span className="no-results-icon">🔍</span>
            <p>Aucun résultat pour "{query}"</p>
            <p className="no-results-hint">Essayez avec d'autres mots-clés</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
